import type { ReactElement } from 'react';
import { Fingerprint, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Sidebar, type SidebarGroup, type SidebarItem } from '@/components/shell/Sidebar';
import { useSetBreadcrumbs, type BreadcrumbItem } from '@/components/shell/ShellContext';
import type { UserProfileDraft } from '@/features/admin-users/api';
import {
  LinkedIdentitiesPanel,
  type LinkedIdentitiesPanelData,
} from '@/features/admin-federated-identities/components/LinkedIdentitiesPanel';

import { UserOverviewSection } from './UserOverviewSection';

const ACTIVE_STATUS = 'ACTIVE';
const OVERVIEW = 'overview';
const IDENTITIES = 'identities';

export interface UserRecordViewProps {
  readonly displayName: string;
  readonly username: string;
  readonly email: string;
  readonly status: string | null;
  readonly roles: readonly string[];
  readonly breadcrumbs: readonly BreadcrumbItem[];
  /** Overview: editable demographics. */
  readonly demographics: UserProfileDraft | undefined;
  readonly demographicsLoading: boolean;
  readonly canEdit: boolean;
  readonly saving: boolean;
  readonly saveError: unknown;
  readonly onSaveProfile: (draft: UserProfileDraft) => void;
  /** Optional linked-identities section (omitted for planes without federation yet). */
  readonly identities?: LinkedIdentitiesPanelData | null;
}

/**
 * First-class user "document" page (PTV2-1451), mirroring the Tenant detail page: an inline two-column
 * shell (contextual sidebar titled USER + main), an identity-band top bar (username + email/role chips),
 * a hash-driven, centered body (Overview + optional Linked identities), and breadcrumbs. Purely
 * presentational + plane-agnostic — the route wrappers feed it their own data + handlers.
 */
export function UserRecordView(props: UserRecordViewProps): ReactElement {
  const { t } = useTranslation();
  const { pathname, hash } = useLocation();
  useSetBreadcrumbs([...props.breadcrumbs]);

  const hasIdentities = props.identities !== undefined && props.identities !== null;
  const hashId = hash.length > 1 ? hash.slice(1) : OVERVIEW;
  const activeId = hashId === IDENTITIES && !hasIdentities ? OVERVIEW : hashId;

  const items: SidebarItem[] = [
    {
      key: OVERVIEW,
      label: t('userRecord.sidebar.overview'),
      to: `${pathname}#${OVERVIEW}`,
      icon: <UserRound className="h-4 w-4" aria-hidden="true" />,
      defaultActive: true,
    },
  ];
  if (hasIdentities) {
    items.push({
      key: IDENTITIES,
      label: t('userRecord.sidebar.identities'),
      to: `${pathname}#${IDENTITIES}`,
      icon: <Fingerprint className="h-4 w-4" aria-hidden="true" />,
    });
  }
  const groups: SidebarGroup[] = [{ key: 'user', heading: t('userRecord.sidebar.title'), items }];

  return (
    <div className="flex h-full">
      <Sidebar groups={groups} />
      <main
        aria-label={props.displayName}
        className="flex-1 flex flex-col min-w-0 min-h-0 overflow-clip"
      >
        <header className="border-b border-border bg-card px-8 py-3">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
              <UserRound className="h-[18px] w-[18px] text-muted-foreground" aria-hidden="true" />
              <span className="truncate">{props.displayName}</span>
              <Badge variant={props.status === ACTIVE_STATUS ? 'secondary' : 'outline'}>
                {props.status !== null && props.status.length > 0
                  ? props.status
                  : t('userRecord.unknownStatus')}
              </Badge>
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
              {props.email.length > 0 && <span className="truncate">{props.email}</span>}
              {props.roles.length > 0 && props.email.length > 0 && <span aria-hidden="true">·</span>}
              {props.roles.length > 0 ? (
                props.roles.map((role) => (
                  <Badge key={role} variant="outline">
                    {role}
                  </Badge>
                ))
              ) : (
                <span>{t('userRecord.noRoles')}</span>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-background">
          <div className="mx-auto max-w-[1100px] px-8 py-6">
            {activeId === OVERVIEW && (
              <UserOverviewSection
                demographics={props.demographics}
                loading={props.demographicsLoading}
                canEdit={props.canEdit}
                saving={props.saving}
                saveError={props.saveError}
                onSave={props.onSaveProfile}
              />
            )}
            {activeId === IDENTITIES && props.identities !== undefined && props.identities !== null && (
              <LinkedIdentitiesPanel {...props.identities} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
